import { useEffect, useMemo, useState } from 'react';
import type { BroadcastGroupSummary } from '@/types/broadcast';
import { Button } from '@/components/ui/button';
import { X, ChevronDown, Calendar, Loader2 } from 'lucide-react';
import BroadcastGroupGrid from './BroadcastGroupGrid';
import { diaPorExtenso, BRT_TZ } from '../utils/calendarUtils';
import { quandoAconteceu } from './BroadcastCalendar';

interface BroadcastDayModalProps {
    /** YYYY-MM-DD no fuso de Brasília. */
    dia: string;
    grupos: BroadcastGroupSummary[];
    /** Os grupos do dia sao buscados no clique, entao o modal abre vazio. */
    carregando?: boolean;
    onClose: () => void;
    onDuplicate: (group: BroadcastGroupSummary) => void;
    onCancel: (group: BroadcastGroupSummary) => void;
    onView: (group: BroadcastGroupSummary) => void;
}

const horaBRT = (isoUtc?: string): string => {
    if (!isoUtc) return '--';
    const d = new Date(isoUtc);
    if (isNaN(d.getTime())) return '--';
    return new Intl.DateTimeFormat('pt-BR', { timeZone: BRT_TZ, hour: '2-digit', minute: '2-digit' }).format(d);
};

/**
 * O dia aberto, agrupado por horário de disparo.
 *
 * Os cards são os MESMOS da aba Execuções (`BroadcastGroupGrid`): um grupo tem
 * que se comportar igual nos dois lugares, e "Ver" abre o mesmo detalhe. Só a
 * quebra por hora é própria daqui — num dia cheio são 20+ grupos em blocos de
 * meia em meia hora, e sem a quebra vira uma lista sem hierarquia.
 */
export default function BroadcastDayModal({
    dia, grupos, carregando, onClose, onDuplicate, onCancel, onView,
}: BroadcastDayModalProps) {
    const [recolhidos, setRecolhidos] = useState<Set<string>>(new Set());

    // Trava a rolagem da pagina atras. Sem isso a roda do mouse, ao chegar no
    // fim da lista do dia, continua rolando a tela de tras — o modal fica
    // parado e a pagina anda sozinha.
    useEffect(() => {
        const anterior = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = anterior; };
    }, []);

    // Esc fecha: com o overlay clicavel, era a unica saida que faltava.
    useEffect(() => {
        const aoTeclar = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', aoTeclar);
        return () => window.removeEventListener('keydown', aoTeclar);
    }, [onClose]);

    const blocos = useMemo(() => {
        const mapa = new Map<string, BroadcastGroupSummary[]>();
        for (const g of grupos) {
            const hora = horaBRT(quandoAconteceu(g));
            const lista = mapa.get(hora);
            if (lista) lista.push(g); else mapa.set(hora, [g]);
        }
        return Array.from(mapa.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    }, [grupos]);

    const totais = useMemo(() => ({
        grupos: grupos.length,
        leads: grupos.reduce((s, g) => s + (g.total_leads ?? 0), 0),
        enviados: grupos.reduce((s, g) => s + (g.total_sent ?? 0), 0),
        paginas: grupos.reduce((s, g) => s + (g.total_pages ?? 0), 0),
    }), [grupos]);

    const alternar = (hora: string) => {
        setRecolhidos(atual => {
            const novo = new Set(atual);
            if (novo.has(hora)) novo.delete(hora); else novo.add(hora);
            return novo;
        });
    };

    const todosRecolhidos = recolhidos.size === blocos.length && blocos.length > 0;

    return (
        <div
            className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4 md:p-8"
            onClick={onClose}
        >
            <div
                className="flex max-h-[88vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-gray-100 bg-white px-6 py-4">
                    <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <h2 className="text-sm font-semibold text-gray-900">{diaPorExtenso(dia)}</h2>
                        <span className="text-xs text-gray-400">
                            {totais.grupos} grupo(s) · {totais.paginas.toLocaleString('pt-BR')} páginas ·{' '}
                            {totais.enviados.toLocaleString('pt-BR')} de {totais.leads.toLocaleString('pt-BR')} leads
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            className="rounded-xl"
                            onClick={() => setRecolhidos(todosRecolhidos ? new Set() : new Set(blocos.map(([h]) => h)))}
                        >
                            {todosRecolhidos ? 'Expandir tudo' : 'Recolher tudo'}
                        </Button>
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Fechar"
                            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
                    {blocos.map(([hora, doBloco]) => {
                        const recolhido = recolhidos.has(hora);
                        const leads = doBloco.reduce((s, g) => s + (g.total_leads ?? 0), 0);
                        const enviados = doBloco.reduce((s, g) => s + (g.total_sent ?? 0), 0);

                        return (
                            <div key={hora} className="overflow-hidden rounded-xl border border-gray-100">
                                <button
                                    type="button"
                                    onClick={() => alternar(hora)}
                                    className="flex w-full items-center justify-between bg-gray-50/70 px-4 py-2.5 hover:bg-gray-50"
                                >
                                    <span className="flex items-center gap-2">
                                        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${recolhido ? '-rotate-90' : ''}`} />
                                        <span className="text-sm font-semibold text-gray-900">{hora}</span>
                                        <span className="text-xs text-gray-400">
                                            {doBloco.length} broadcast(s)
                                        </span>
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        {enviados.toLocaleString('pt-BR')} de {leads.toLocaleString('pt-BR')} leads
                                    </span>
                                </button>

                                {!recolhido && (
                                    <div className="p-3">
                                        <BroadcastGroupGrid
                                            groups={doBloco}
                                            onView={onView}
                                            onDuplicate={onDuplicate}
                                            onCancel={onCancel}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {carregando && blocos.length === 0 && (
                        <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-400">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Carregando o dia...
                        </div>
                    )}

                    {!carregando && blocos.length === 0 && (
                        <p className="py-8 text-center text-sm text-gray-400">Nenhum broadcast neste dia.</p>
                    )}
                </div>
            </div>
        </div>
    );
}
